import { useState, useEffect, useCallback } from 'react';
import { generationCostService, GenerationCost } from '../lib/generationCostService';

export const useGenerationCosts = () => {
  const [costs, setCosts] = useState<Record<string, number>>({});
  const [allCosts, setAllCosts] = useState<GenerationCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load all generation costs
  const loadCosts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const mainCosts = await generationCostService.getMainServiceCosts();
      const allGenerationCosts = await generationCostService.getAllGenerationCosts();
      
      setCosts(mainCosts);
      setAllCosts(allGenerationCosts);
    } catch (err) {
      console.error('Error loading generation costs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load costs');
    } finally {
      setLoading(false);
    }
  }, []);

  // Get cost for a specific service
  const getServiceCost = useCallback(async (serviceName: string): Promise<number> => {
    try {
      return await generationCostService.getServiceCost(serviceName);
    } catch (err) {
      console.error(`Error getting cost for ${serviceName}:`, err);
      return 0;
    }
  }, []);

  // Update cost for a service (admin function)
  const updateServiceCost = useCallback(async (serviceName: string, newCost: number): Promise<boolean> => {
    try {
      const success = await generationCostService.updateGenerationCost(serviceName, newCost);
      if (success) {
        // Reload costs to reflect changes
        await loadCosts();
      }
      return success;
    } catch (err) {
      console.error(`Error updating cost for ${serviceName}:`, err);
      return false;
    }
  }, [loadCosts]);

  // Clear cache
  const clearCache = useCallback(() => {
    generationCostService.clearCache();
  }, []);

  // Load costs on mount
  useEffect(() => {
    loadCosts();
  }, [loadCosts]);

  return {
    costs,
    allCosts,
    loading,
    error,
    getServiceCost,
    updateServiceCost,
    clearCache,
    reloadCosts: loadCosts
  };
}; 